/**
 * History model (docs/history-ux.md) — pure, side-effect-free, unit-tested in history.test.ts.
 *
 *   rangeWindow     7d · 30d · 90d · YTD · All → the `[from, to)` ISO-day window every History
 *                   query uses (`/auth/performance` and the settled-orders list)
 *   daySeries       `group_by=day` groups → a sorted, gap-filled, accumulated daily series for the
 *                   cumulative P&L chart (the API returns day groups unsorted and skips empty days;
 *                   see FRICTION.md)
 *   heatLevel       a day's net → a −4…4 heat bucket for the compact calendar
 *   sortBreakdown   venue / league / model groups, sorted by net P&L
 *   filterSettled   the client-side half of the bets-list filters (venue, league, search, and
 *                   dropping `cancelled` rows) — the server can't filter on those
 *
 * Days are UTC calendar days throughout: the API's `from`/`to` are UTC midnights (it echoes
 * `2026-10-01T00:00:00.000Z`) and its day keys follow them.
 */
import { settledProfit, type OrderLike } from "./portfolio.js";

/* -------------------------------------------------------------------- range */

export type Range = "7d" | "30d" | "90d" | "ytd" | "all";
export const RANGES: { id: Range; label: string }[] = [
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
  { id: "90d", label: "90d" },
  { id: "ytd", label: "YTD" },
  { id: "all", label: "All" },
];
export const DEFAULT_RANGE: Range = "30d";

export function isRange(s: string | null | undefined): s is Range {
  return s === "7d" || s === "30d" || s === "90d" || s === "ytd" || s === "all";
}

/**
 * "All time" needs an explicit early `from`: without one `/auth/performance` defaults to the last
 * 90 days. 1970-01-01 is accepted and returns the whole book.
 */
export const ALL_FROM = "1970-01-01";

/** ISO day (YYYY-MM-DD, UTC) of a timestamp. */
export function isoDay(t: number | Date): string {
  return new Date(typeof t === "number" ? t : t.getTime()).toISOString().slice(0, 10);
}

/** `day` shifted by `n` UTC days. */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * The `[from, to)` window for a range, as ISO days. `to` is always tomorrow (the API's `to` is an
 * exclusive boundary, so this includes everything settled today). "7d" is today and the 6 days
 * before it; YTD starts on 1 January of the current (UTC) year; All starts at {@link ALL_FROM}.
 */
export function rangeWindow(range: Range, now: number | Date): { from: string; to: string } {
  const today = isoDay(now);
  const to = addDays(today, 1);
  switch (range) {
    case "7d":
      return { from: addDays(today, -6), to };
    case "30d":
      return { from: addDays(today, -29), to };
    case "90d":
      return { from: addDays(today, -89), to };
    case "ytd":
      return { from: `${today.slice(0, 4)}-01-01`, to };
    case "all":
      return { from: ALL_FROM, to };
  }
}

/** How a range reads in a sentence: "in the last 30 days", "this year", "" for all time. */
export function rangePhrase(range: Range): string {
  switch (range) {
    case "7d":
      return "in the last 7 days";
    case "30d":
      return "in the last 30 days";
    case "90d":
      return "in the last 90 days";
    case "ytd":
      return "this year";
    case "all":
      return "";
  }
}

/** The `[from, to)` window of the calendar month containing ISO day `month` (any day in it). */
export function monthWindow(month: string): { from: string; to: string } {
  const [y, m] = month.split("-").map(Number);
  const from = new Date(Date.UTC(y, m - 1, 1)).toISOString().slice(0, 10);
  const to = new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);
  return { from, to };
}

/** The first day of the month `n` months from the month of `month`. */
export function addMonths(month: string, n: number): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 10);
}

/* --------------------------------------------------------------- day series */

/** The fields of a performance group History reads. */
export interface GroupLike {
  key: string;
  label?: string | null;
  staked?: number | null;
  net_pl?: number | null;
  roi?: number | null;
  settled_count?: number | null;
  wins?: number | null;
  losses?: number | null;
  pushes?: number | null;
}

export interface DayPoint {
  day: string;
  /** That day's net P&L (0 on a day with nothing settled). */
  net: number;
  /** Running total from the first day of the series through this one. */
  cum: number;
  staked: number;
  wins: number;
  losses: number;
  pushes: number;
  /** True when the API returned a group for this day. */
  active: boolean;
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
/** Never draw more than this many days (≈ 10 years) — guards a bad `from`. */
const MAX_DAYS = 3700;

/**
 * Sort the day groups, fill every missing day in `[from, to)` with 0 and accumulate. With
 * `trimToData` the series starts at the first day that has data instead of at `from` (for All
 * time, whose `from` is 1970). Keys that aren't ISO days or fall outside the window are ignored;
 * a duplicate key is summed.
 */
export function daySeries(
  groups: GroupLike[],
  window: { from: string; to: string },
  opts: { trimToData?: boolean } = {},
): DayPoint[] {
  const byDay = new Map<string, GroupLike[]>();
  for (const g of groups) {
    if (!DAY_RE.test(g.key) || g.key < window.from || g.key >= window.to) continue;
    const list = byDay.get(g.key);
    if (list) list.push(g);
    else byDay.set(g.key, [g]);
  }
  const keys = [...byDay.keys()].sort();
  let start = window.from;
  if (opts.trimToData) {
    if (keys.length === 0) return [];
    start = keys[0];
  }
  const last = addDays(window.to, -1);
  const out: DayPoint[] = [];
  let cum = 0;
  for (let d = start; d <= last && out.length < MAX_DAYS; d = addDays(d, 1)) {
    const gs = byDay.get(d) ?? [];
    let net = 0, staked = 0, wins = 0, losses = 0, pushes = 0;
    for (const g of gs) {
      net += g.net_pl ?? 0;
      staked += g.staked ?? 0;
      wins += g.wins ?? 0;
      losses += g.losses ?? 0;
      pushes += g.pushes ?? 0;
    }
    cum += net;
    out.push({ day: d, net, cum, staked, wins, losses, pushes, active: gs.length > 0 });
  }
  return out;
}

/* ---------------------------------------------------------------- heat map */

/**
 * A day's heat level for the compact calendar: 0 for nothing (or a flat day), ±1…±4 by how big
 * the day was against the month's biggest |net|. Square-root scaled, so one huge day doesn't wash
 * every other day out to level 1.
 */
export function heatLevel(net: number | null | undefined, maxAbs: number): number {
  if (net == null || !Number.isFinite(net) || Math.abs(net) < 0.005 || !(maxAbs > 0)) return 0;
  const ratio = Math.min(1, Math.abs(net) / maxAbs);
  const level = Math.max(1, Math.ceil(Math.sqrt(ratio) * 4));
  return net > 0 ? level : -level;
}

/** The biggest |net| among the groups (the calendar's heat scale). */
export function maxAbsNet(groups: GroupLike[]): number {
  let m = 0;
  for (const g of groups) m = Math.max(m, Math.abs(g.net_pl ?? 0));
  return m;
}

export interface CalendarCell {
  day: string;
  /** Day of the month (1-31). */
  date: number;
}

/** Monday-first weeks of the month containing `month`; leading/trailing blanks are null. */
export function monthWeeks(month: string): (CalendarCell | null)[][] {
  const { from, to } = monthWindow(month);
  const [y, m] = from.split("-").map(Number);
  const leading = (new Date(Date.UTC(y, m - 1, 1)).getUTCDay() + 6) % 7;
  const cells: (CalendarCell | null)[] = Array.from({ length: leading }, () => null);
  for (let d = from, n = 1; d < to; d = addDays(d, 1), n++) cells.push({ day: d, date: n });
  while (cells.length % 7) cells.push(null);
  const weeks: (CalendarCell | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/* --------------------------------------------------------------- breakdowns */

/** Groups to show in a breakdown: by net P&L (best first), then staked, then label. */
export function sortBreakdown<G extends GroupLike>(groups: G[]): G[] {
  return [...groups].sort(
    (a, b) =>
      (b.net_pl ?? 0) - (a.net_pl ?? 0) ||
      (b.staked ?? 0) - (a.staked ?? 0) ||
      (a.label ?? a.key).localeCompare(b.label ?? b.key),
  );
}

/**
 * The rows a breakdown shows: groups with at least one bet that settled to a result, best net
 * first. A group whose only orders were cancelled (e.g. a venue that rejected the one order sent
 * there) comes back from the API as `0-0-0`, `$0` and says nothing.
 */
export function breakdownRows<G extends GroupLike>(groups: G[]): G[] {
  return sortBreakdown(groups.filter((g) => settledBets(g) > 0 || Math.abs(g.net_pl ?? 0) >= 0.005));
}

/** The model breakdown says nothing while every bet is "No model" (or there are no groups). */
export function modelBreakdownUseful(groups: GroupLike[]): boolean {
  return groups.some((g) => g.key !== "none" && (g.label ?? "").toLowerCase() !== "no model");
}

/** `12-9-1` (W-L-P). */
export function record(g: { wins?: number | null; losses?: number | null; pushes?: number | null }): string {
  return `${g.wins ?? 0}-${g.losses ?? 0}-${g.pushes ?? 0}`;
}

/** Win rate over decided bets (wins ÷ (wins + losses)), or null with none decided. */
export function winRate(g: { wins?: number | null; losses?: number | null }): number | null {
  const w = g.wins ?? 0;
  const l = g.losses ?? 0;
  return w + l > 0 ? w / (w + l) : null;
}

/**
 * Bets that settled to a result (W + L + P). The API's `settled_count` also counts `cancelled`
 * orders, which never took effect (see FRICTION.md), so History counts these instead.
 */
export function settledBets(g: { wins?: number | null; losses?: number | null; pushes?: number | null }): number {
  return (g.wins ?? 0) + (g.losses ?? 0) + (g.pushes ?? 0);
}

/* ------------------------------------------------------------- list filters */

export type ResultFilter = "win" | "lose" | "push";
export const RESULT_FILTERS: { id: ResultFilter; label: string }[] = [
  { id: "win", label: "Won" },
  { id: "lose", label: "Lost" },
  { id: "push", label: "Push" },
];

export interface ListFilters {
  /** Server-side (`result=`), repeated here so a stale page can't show the wrong rows. */
  result?: ResultFilter | null;
  /** partner_id — client-side (the API has no venue filter on orders). */
  venue?: string | null;
  /** league_id — client-side, via the contest lookup (order rows carry no league). */
  league?: string | null;
  /** Outcome or team text — client-side. */
  q?: string;
}

/** Does any filter run on the client (so the list can only search what's loaded)? */
export function hasClientFilter(f: ListFilters): boolean {
  return !!f.venue || !!f.league || !!f.q?.trim();
}

const RESULTS = new Set(["win", "lose", "push"]);
const norm = (s: string | null | undefined): string => (s ?? "").toLowerCase();

/**
 * The bets the list shows from the loaded rows: settled to a result (`cancelled` rows, which
 * `settled=true` also returns, never took effect), then the venue / league / search filters.
 * `leagueOf` maps a contest id to its league id (undefined while unknown — such rows are kept out
 * of a league filter until their contest loads). `gameText` gives the searchable game text (team
 * names) for a contest.
 */
export function filterSettled<O extends OrderLike>(
  orders: O[],
  f: ListFilters,
  leagueOf: (contestId: string) => string | undefined,
  gameText: (contestId: string) => string = () => "",
): O[] {
  const q = f.q?.trim().toLowerCase() ?? "";
  return orders.filter((o) => {
    const r = norm(o.result);
    if (!RESULTS.has(r)) return false;
    if (f.result && r !== f.result) return false;
    if (f.venue && (o.partner_id ?? o.partner?.partner_id) !== f.venue) return false;
    if (f.league && (!o.contest_id || leagueOf(o.contest_id) !== f.league)) return false;
    if (q) {
      const hay = `${o.title ?? ""} ${o.contest_id ? gameText(o.contest_id) : ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

/**
 * A settled bet's P&L exactly as `/auth/performance` sums it: `net_winnings − amount` (the
 * execution docs' definition). On fee-charging venues a LOSING order comes back with
 * `net_winnings = −fee` (the docs say 0 — see FRICTION.md), so this is −stake − fee and the rows
 * add up to the totals above them. Without `net_winnings`, Portfolio's reading stands in.
 */
export function betProfit(o: OrderLike): number {
  return typeof o.net_winnings === "number" && Number.isFinite(o.net_winnings) ? o.net_winnings - (o.amount ?? 0) : settledProfit(o);
}

/* ------------------------------------------------------------------- money */

export type Book = "USD" | "ATLAS";

const ATLAS_FMT = new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const USD_FMT = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

/** `$1,234.50` / `1,234.5 ATLAS`. */
export function fmtAmt(n: number | null | undefined, ccy: Book): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return ccy === "USD" ? USD_FMT.format(n) : `${ATLAS_FMT.format(n)} ATLAS`;
}

/** `+$12.40` / `−$9.92` / `$0.00` (a real minus glyph; flat under half a cent). */
export function fmtSignedAmt(n: number | null | undefined, ccy: Book): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const sign = n >= 0.005 ? "+" : n <= -0.005 ? "−" : "";
  return `${sign}${fmtAmt(Math.abs(n), ccy)}`;
}

/** Axis-sized: `$1.2k`, `−$350`, `12k ATLAS` → `12k`. */
export function fmtCompact(n: number, ccy: Book): string {
  const a = Math.abs(n);
  const sign = n < 0 ? "−" : "";
  const body = a >= 1e6 ? `${trim(a / 1e6)}M` : a >= 1e3 ? `${trim(a / 1e3)}k` : a >= 10 || a === 0 ? `${Math.round(a)}` : trim(a);
  return ccy === "USD" ? `${sign}$${body}` : `${sign}${body}`;
}
const trim = (x: number): string => (Math.round(x * 10) / 10).toString();

/** "Nice" axis ticks covering [lo, hi] (always including 0 when the range crosses it). */
export function niceTicks(lo: number, hi: number, count = 4): number[] {
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [0];
  if (hi - lo < 1e-9) {
    const pad = Math.max(1, Math.abs(hi) * 0.5);
    lo -= pad;
    hi += pad;
  }
  const raw = (hi - lo) / Math.max(1, count);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag;
  const start = Math.floor(lo / step) * step;
  const out: number[] = [];
  for (let v = start; v <= hi + step * 0.999 && out.length < 12; v += step) out.push(Math.abs(v) < step * 1e-6 ? 0 : v);
  return out;
}
